import { Variable } from '~/packages/variable/variables.typing'
import { dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForCurrentOnly } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import monitorCourseClassesMetricsStatsAction from './monitorCourseClassesMetricsStats.action.json' with { type: 'json' }
import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import prefixProperties from '~/prix/utils/prefixProperties'

const courseClassesMetricsShape = {
  id: string().label('ID', 'Identificador', 'Código'),
  eventId: string().isOptional().label('ID do evento'),
  date: dateTime().isOptional().label('Data da turma'),
  productCode: string().isOptional().label('Código do produto'),
  name: string().isOptional().label('Nome do produto'),
  eventName: string().isOptional().label('Nome do evento'),
  organizationUnit: string().isOptional().label('Unidade organizacional'),
  uf: enumerated({
    Brasil: 'Brasil',
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
  }).label('Unidade Federativa'),
  registeredCount: number().isOptional().label('Número de inscritos'),
  finishedCount: number().isOptional().label('Número de concluintes'),
  createdAt: dateTime().isOptional().label('Data de criação'),
  updatedAt: dateTime().isOptional().label('Data de atualização'),
}

const combinedShape = object({
  ...prefixProperties('courseClassesMetrics.', courseClassesMetricsShape),
})

export const monitorCourseClassesMetricsExpression = expression(combinedShape.shape)

export const monitorCourseClassesMetricsVariable: Variable = {
  key: 'monitorCourseClassesMetrics',
  categoryKey: 'monitorPortfolioCourseClassesMetrics',
  actionDefinitionKey: monitorCourseClassesMetricsStatsAction.key,
  categoryKeys: ['monitorPortfolioCourseClassesMetrics'],
  aggregationMode: 'sum',
  title: 'Registro de participantes em turma',
  unitSingular: 'registro',
  unitPlural: 'registros',
  grammaticalGender: 'male',
  timeType: 'currentOnly',
  shape: combinedShape,
  betterDensity: 'more',
  customValue: {
    type: 'expression',
    kind: 'function',
    name: 'sum',
    arguments: [
      {
        type: 'expression',
        kind: 'property',
        property: 'courseClassesMetrics.registeredCount',
      },
    ],
  },
  relevance: 0.3,
  basicSelect: [
    'courseClassesMetrics.date',
    'courseClassesMetrics.eventName',
    'courseClassesMetrics.organizationUnit',
  ],
  detailedSelect: [],
  timeSeries: {},
  assertions: prepareAssertions(monitorCourseClassesMetricsExpression),
  compatibleChildrenTypes: ['country', 'macroRegion', 'state'],
}

const monitorCourseClassesMetricsStatsDefinition = defineAction({
  ...monitorCourseClassesMetricsStatsAction,
  input: object({
    variable,
    highlight: monitorCourseClassesMetricsExpression.isOptional(),
    filter: monitorCourseClassesMetricsExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForCurrentOnly().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default monitorCourseClassesMetricsStatsDefinition
